import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
} from 'react';
import { COMMON_ERRORS, COMMON_MESSAGES } from '@utils/constants';
import AppSnackbar from '@contexts/Snackbar/AppSnackbar';
import { getMultiErrors } from '@utils/Functions';

export const SnackBarContext = createContext();

export const SNACKBAR_TYPES = {
  error: 'error',
  success: 'success',
};

export function SnackBarProvider({ children }) {
  const initialValues = {
    message: '',
    open: false,
    type: SNACKBAR_TYPES.error,
  };
  const [snackbar, setSnackbar] = useState(initialValues);

  const getErrorMessage = useCallback(
    (error, fallbackMessage = COMMON_MESSAGES.error) => {
      if (typeof error === 'string') return error;

      const message =
        error?.detail ||
        error?.response?.data?.detail ||
        error?.response_message ||
        error?.message;

      if (Array.isArray(message)) {
        return getMultiErrors(message);
      }

      if (message) {
        return COMMON_ERRORS[message] || message || fallbackMessage;
      }

      return fallbackMessage;
    },
    [],
  );

  const showSuccessSnackbar = useCallback(
    (message = COMMON_MESSAGES.success) => {
      const messageCurated =
        typeof message === 'string' ? message : COMMON_MESSAGES.success;
      setSnackbar({
        open: true,
        type: SNACKBAR_TYPES.success,
        message: messageCurated,
      });
    },
    [],
  );

  const showErrorSnackbar = useCallback(
    (message = COMMON_MESSAGES.error) => {
      setSnackbar({
        open: true,
        type: SNACKBAR_TYPES.error,
        message: getErrorMessage(message),
      });
    },
    [getErrorMessage],
  );

  const addSnackbar = useCallback(
    ({ open = true, type, message }) => setSnackbar({ open, type, message }),
    [],
  );

  const value = useMemo(
    () => ({ showErrorSnackbar, showSuccessSnackbar, addSnackbar }),
    [showErrorSnackbar, showSuccessSnackbar, addSnackbar],
  );

  return (
    <SnackBarContext.Provider value={value}>
      {children}
      <AppSnackbar snackbar={snackbar} setSnackbar={setSnackbar} />
    </SnackBarContext.Provider>
  );
}

export const useSnackBars = () => useContext(SnackBarContext);
